import React, { useEffect, useState } from "react";
import { getToken } from "./auth";
import logo from "./utils/logo2.png";
import { FaPencilAlt } from "react-icons/fa";

const BACKEND_URL = "https://staging.parkengage.ai/api";

export default function ManageQna() {
  const [faqList, setFaqList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");

  // Editing states
  const [editingIndex, setEditingIndex] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");

  const fetchData = async () => {
    const res = await fetch(`${BACKEND_URL}/get_qna`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });

    if (!res.ok) return alert("Failed to fetch Q&A");

    const data = await res.json();
    setFaqList(data);
    setFiltered(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // SEARCH FILTER
  useEffect(() => {
    const s = search.toLowerCase();
    const f = faqList.filter(
      (x) =>
        x.question.toLowerCase().includes(s) ||
        x.answer.toLowerCase().includes(s)
    );
    setFiltered(f);
  }, [search, faqList]);

  const toggleSelect = (q) => {
    if (selected.includes(q))
      setSelected(selected.filter((x) => x !== q));
    else setSelected([...selected, q]);
  };

  const deleteSelected = async () => {
    if (selected.length === 0) return alert("No questions selected.");

    const ok = window.prompt(
      `Type DELETE to confirm removing ${selected.length} items:`
    );
    if (ok !== "DELETE") return;

    const res = await fetch(`${BACKEND_URL}/delete_qna`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ questions: selected }),
    });

    if (!res.ok) return alert("Delete failed.");

    alert("Deleted successfully!");
    fetchData();
    setSelected([]);
  };

  // SAVE EDIT
  const saveEdit = async (originalQuestion) => {
    const res = await fetch(`${BACKEND_URL}/update_qna`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        old_question: originalQuestion,
        new_question: editQuestion,
        new_answer: editAnswer,
      }),
    });

    if (!res.ok) return alert("Update failed.");

    alert("Updated successfully!");
    setEditingIndex(null);
    fetchData();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 p-6">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 bg-white/60 backdrop-blur-lg p-4 rounded-xl shadow-lg">

        {/* LOGO */}
        <img
          src={logo}
          alt="Logo"
          className="w-32 h-auto object-contain"
        />

        {/* TITLE */}
        <h1 className="text-3xl font-bold text-indigo-700 drop-shadow">
          Manage Q&A
        </h1>

        {/* SEARCH + BUTTONS */}
        <div className="flex items-center gap-3">
          <input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-2 rounded-lg border shadow bg-white focus:ring-2 focus:ring-indigo-400 transition"
          />

          <button
            onClick={deleteSelected}
            className="py-2 px-4 rounded-xl bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition-transform hover:scale-105"
          >
            Delete
          </button>

          <button
            onClick={() => (window.location.href = "/")}
            className="py-2 px-4 rounded-xl bg-gray-300 font-semibold shadow hover:bg-gray-400 transition-transform hover:scale-105"
          >
            Back
          </button>
        </div>
      </div>

      {/* QNA LIST */}
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-5 border animate-fadeIn">
        {filtered.length === 0 ? (
          <p className="text-center py-10 text-gray-500">No results found.</p>
        ) : (
          filtered.map((item, i) => {
            const isEditing = editingIndex === i;

            return (
              <div
                key={i}
                className="flex items-start justify-between gap-3 border-b py-4 hover:bg-indigo-50 transition rounded-lg"
              >
                <div className="flex items-start gap-3">

                  {/* CHECKBOX */}
                  <input
                    type="checkbox"
                    checked={selected.includes(item.question)}
                    onChange={() => toggleSelect(item.question)}
                    className="mt-1 w-5 h-5 cursor-pointer accent-indigo-600"
                  />

                  <div>
                    {isEditing ? (
                      <>
                        <input
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          className="w-full px-2 py-1 border rounded mb-2"
                          autoFocus
                          onKeyDown={(e) =>
                            e.key === "Enter" && saveEdit(item.question)
                          }
                        />
                        <textarea
                          value={editAnswer}
                          onChange={(e) => setEditAnswer(e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                          onKeyDown={(e) =>
                            e.key === "Enter" && saveEdit(item.question)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-indigo-700">
                          {item.question}
                        </p>
                        <p className="text-gray-700 text-sm mt-1">
                          {item.answer}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                {isEditing ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(item.question)}
                      className="px-3 py-1 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
                    >
                      Save
                    </button>

                    <button
                      onClick={() => setEditingIndex(null)}
                      className="px-3 py-1 bg-gray-400 text-white rounded-lg shadow hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingIndex(i);
                      setEditQuestion(item.question);
                      setEditAnswer(item.answer);
                    }}
                    className="p-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 flex items-center justify-center hover:scale-110 transition"
                  >
                    <FaPencilAlt className="text-white text-lg" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
